import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import autoMixSpectra from '@client/utils/autoMixSpectra';
import { calculateRelativeSpectrogramRanges } from '@client/utils/calculateRelativeSpectrogramRanges';
import i18n from '@client/utils/i18n';
import { SpectrogramPoint } from '@ledium/types';
import { Button, Grid } from '@mui/material';

import SpectrumMixerChannelSettingsCarousel from './SpectrumMixerChannelSettingsCarousel';
import SpectrumMixerChannelSettingsGrid from './SpectrumMixerChannelSettingsGrid';
import { MixerChannel, MixerSettings } from './SpectrumMixerChannelSettingsItem';

const SpectrumMixerChannelSettings = () => {
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const lightChannels = formData.lightChannels as MixerChannel[];
    const settings = formData.settings as MixerSettings;
    const lightRecipe = formData.lightRecipe as { spectrogram?: SpectrogramPoint[] } | null;

    function onSliderChange(channelId: string, value: number) {
        const updated = lightChannels.map((channel) => {
            if (channel.id === channelId) {
                const channelMaxIntensity = 2 ** channel.resolution - 1;
                const intensityRatio = value / channelMaxIntensity;
                return {
                    ...channel,
                    intensityPercent: Math.round(intensityRatio * 100),
                    intensity: Math.round(+value),
                    intensityRanges: calculateRelativeSpectrogramRanges(channel.spectrogramRanges, intensityRatio)
                };
            }
            return channel;
        });
        pageActions.formData.updateFormItem({ lightChannels: updated });
    }

    function onIntensityChange(channelId: string, valueParam: number) {
        let value = valueParam;
        const updated = lightChannels.map((channel) => {
            if (channel.id === channelId) {
                const unitMax = channel.spectrogramRanges[settings.intensityUnit].sum;
                const channelMaxIntensity = 2 ** channel.resolution - 1;
                value = value < 0 ? 0 : value;
                value = value > unitMax || (channel.resolution === 1 && value > 0) ? unitMax : value;
                const intensityRatio = value / unitMax;

                return {
                    ...channel,
                    intensityPercent: Math.round(intensityRatio * 100),
                    intensity: Math.round(channelMaxIntensity * intensityRatio),
                    intensityRanges: calculateRelativeSpectrogramRanges(channel.spectrogramRanges, intensityRatio)
                };
            }
            return channel;
        });
        pageActions.formData.updateFormItem({ lightChannels: updated });
    }

    function onAutoMix() {
        const recipe = lightRecipe?.spectrogram;
        if (!recipe) {
            return;
        }

        const ratios = autoMixSpectra(recipe, lightChannels, settings.intensityUnit);
        const updated = lightChannels.map((channel, i) => {
            const intensityRatio = ratios[i];
            const channelMaxIntensity = 2 ** channel.resolution - 1;
            return {
                ...channel,
                intensityPercent: Math.round(intensityRatio * 100),
                intensity: Math.round(channelMaxIntensity * intensityRatio),
                intensityRanges: calculateRelativeSpectrogramRanges(channel.spectrogramRanges, intensityRatio)
            };
        });
        pageActions.formData.updateFormItem({ lightChannels: updated });
    }

    if (!lightChannels.length) {
        return null;
    }

    return (
        <>
            {!formData.readOnly && (
                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Button variant="outlined" disabled={!lightRecipe?.spectrogram} onClick={onAutoMix}>
                        {i18n('spectrum_mix_auto_mix')}
                    </Button>
                </Grid>
            )}
            {settings.carouselView ? (
                <SpectrumMixerChannelSettingsCarousel onSliderChange={onSliderChange} onIntensityChange={onIntensityChange} />
            ) : (
                <SpectrumMixerChannelSettingsGrid
                    lightChannels={lightChannels}
                    onSliderChange={onSliderChange}
                    onIntensityChange={onIntensityChange}
                    formData={formData}
                />
            )}
        </>
    );
};

export default React.memo(SpectrumMixerChannelSettings);
